import React, { useEffect, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import marketingSolutionServices from "../../../../services/marketingSolution.services";
import _ from "lodash";
import { formatDate } from "../../../../utils/helper";
import { Button, Modal } from "../../../../components/common";
// import history from "../../../../utils/history";
import appRoutes from "../../../../routes/app.routes";
import { useSelector } from "react-redux";
import { rootReducersState } from "../../../../reducers";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";

const PREVIEW_SNIPPET_MAX = 120;
const SUBJECT_SNIPPET_MAX = 80;

export const CampaignHeaderActions = () => {
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const companySlug = sessionReducer.currentCompany.slug;
  const navigate = useNavigate();

  return (
    <>
      <Button
        onClick={() =>
          navigate({
            pathname: appRoutes.emailTemplates.generatePath(companySlug),
          })
        }
      >
        View Templates
      </Button>
      <Button
        onClick={() =>
          navigate(appRoutes.emailCampaign.generatePath(companySlug))
        }
      >
        Create Campaign
      </Button>
      <Button
        onClick={() => {
          navigate(appRoutes.emailEditor.generatePath(companySlug), {
            state: { id: uuidv4(), is_campaign: false },
          });
        }}
      >
        Create Template
      </Button>
    </>
  );
};

const CampaignList = () => {
  const pageSize = 20;
  const [page, setPage] = useState(1);
  const [campaignList, setCampaignList] = useState<any>([]);
  const [pagination, setPagination] = useState<any>({});
  const [isFetching, setIsFetching] = useState(false);
  const [expandedTextModal, setExpandedTextModal] = useState<{
    visible: boolean;
    title: string;
    body: string;
  }>({ visible: false, title: "", body: "" });

  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const companySlug = sessionReducer.currentCompany.slug;
  const navigate = useNavigate();
  const handlePageChange = async (pageNumber) => {
    setPage(pageNumber);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      try {
        const response: any = await marketingSolutionServices.getCampaignList({
          page,
          pageSize,
        });
        if (response.flag) {
          setCampaignList(response.data);
          setPagination(response.pagination);
        } else {
          console.error("Failed to fetch campaign list:", response.message);
        }
        setIsFetching(false);
      } catch (error) {
        console.error("Error fetching campaign list:", error);
      }
      setIsFetching(false);
    };

    fetchData();
  }, [page]);

  const showDataAndTime = (dateString) => {
    return moment(dateString).format("YYYY-MM-DD HH:mm:ss");
  };

  const renderSnippetWithViewMore = (
    raw: string,
    snippetMax: number,
    modalTitle: string
  ) => {
    const text = String(raw ?? "").trim();
    if (!text) {
      return "—";
    }
    if (text.length <= snippetMax) {
      return <span className="campaign-inline-text">{text}</span>;
    }
    const snippet = `${text.slice(0, snippetMax).trim()}…`;
    return (
      <span className="campaign-cell-truncate-wrapper">
        <span className="campaign-inline-text campaign-inline-text--clamp">
          {snippet}
        </span>
        <button
          type="button"
          className="campaign-text-view-more"
          onClick={() =>
            setExpandedTextModal({
              visible: true,
              title: modalTitle,
              body: text,
            })
          }
        >
          View more
        </button>
      </span>
    );
  };

  return (
    <>
      <div className="tab-content">
        <TableContainer component={Paper} className="campaign-table-container">
          <Table className="table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="column campaign-col-name">
                  Campaign Name
                </TableCell>
                <TableCell className="column campaign-col-sender">
                  Sender Name
                </TableCell>
                <TableCell className="column campaign-col-email">
                  Sender Email
                </TableCell>
                <TableCell className="column campaign-col-preview">
                  Preview
                </TableCell>
                <TableCell className="column campaign-col-tags">Tags</TableCell>
                <TableCell className="column campaign-col-subject">
                  Subject
                </TableCell>
                <TableCell className="column campaign-col-status">
                  Status
                </TableCell>
                <TableCell className="column campaign-col-date">Date</TableCell>
                <TableCell className="column campaign-col-action">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={9} style={{ textAlign: "center" }}>
                    <CircularProgress color="secondary" />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {!_.isEmpty(campaignList) &&
                    campaignList?.map((item: any, index) => (
                      <TableRow key={index}>
                        <TableCell className="campaign-col-name">
                          {_.get(item, "campaign_name", "")}
                        </TableCell>
                        <TableCell className="campaign-col-sender">
                          {_.get(item, "sender_name", "")}
                        </TableCell>
                        <TableCell className="campaign-col-email">
                          {_.get(item, "sender_email", "")}
                        </TableCell>
                        <TableCell className="campaign-col-preview">
                          {renderSnippetWithViewMore(
                            _.get(item, "preview_text", ""),
                            PREVIEW_SNIPPET_MAX,
                            "Preview text"
                          )}
                        </TableCell>
                        <TableCell className="campaign-col-tags">
                          {item.campaign_tags === -1
                            ? "All subscribers in audience"
                            : _.get(item, "tags.tag_name", "")}
                        </TableCell>
                        <TableCell className="campaign-col-subject">
                          {renderSnippetWithViewMore(
                            _.get(item, "subject", ""),
                            SUBJECT_SNIPPET_MAX,
                            "Subject"
                          )}
                        </TableCell>
                        <TableCell className="campaign-col-status">
                          {item.is_sent === 0 &&
                          !_.isEmpty(_.get(item, "schedule_at")) ? (
                            <Tooltip
                              title={showDataAndTime(
                                _.get(item, "schedule_at", "")
                              )}
                              placement="top"
                            >
                              <span>Scheduled</span>
                            </Tooltip>
                          ) : item.is_sent === 1 ? (
                            "Sent"
                          ) : (
                            "Not sent yet"
                          )}
                        </TableCell>
                        <TableCell className="campaign-col-date">
                          {formatDate(_.get(item, "created_at", ""))}
                        </TableCell>
                        <TableCell className="campaign-col-action">
                          <Button
                            onClick={() =>
                              navigate(
                                appRoutes.startCampaign.generatePath(
                                  companySlug,
                                  item.campaign_id
                                )
                              )
                            }
                            className="btn btn-primary"
                          >
                            {item.is_sent === 0 ? "Send Now" : "Resend"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </>
              )}
            </TableBody>
          </Table>
          <div className="custom-pagination">
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={handlePageChange}
            />
          </div>
        </TableContainer>
      </div>

      <Modal
        visible={expandedTextModal.visible}
        onClose={() =>
          setExpandedTextModal((s) => ({ ...s, visible: false }))
        }
        title={expandedTextModal.title}
        size="medium"
        className="campaign-expanded-text-modal"
      >
        <div className="campaign-expanded-text-body">
          {expandedTextModal.body}
        </div>
      </Modal>
    </>
  );
};

export default CampaignList;
